import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SmartButton from "../ui/buttons/SmartButton";
import SmallAlert from "../ui/alerts/SmallAlert";
import VerificationModal from "../ui/popup/VerificationModal";
import { store } from "../../store";
import { loginRdx } from "../../store/redux_auth/authSlice";
import {
  getReferralCookie,
  deleteReferralCookie,
} from "../../utils/cookies/cookieHelpers";
import { trackSignIn } from "../tracking/utils/events";
import { accountApi } from "../../api/account";
import SocialAuth from "../../pages/publics/utils/socialauth/SocialAuth";
import TermsConsentNotice from "../ui/auth/TermsConsentNotice";
import { associateAuthenticatedInstallationIfNative } from "../../utils/native/installationAssociation";
import { isPasskeyUiAvailable } from "../../utils/auth/webauthnHelpers";
import { loginWithPasskey } from "../../utils/auth/passkeyLoginHelpers";
import { ApiClient } from "../../api/ApiClient";

function AuthForm({
  redirectPath,
  isAdmin = false,
  isModalMode = false,
  mobileTransaction = false,
}) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [currentErrors, setCurrentErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [buttonState, setButtonState] = useState("idle");

  // MFA State
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupStep, setPopupStep] = useState("idle");
  const [mfaSetupToken, setMfaSetupToken] = useState(null);

  // Dynamic MFA type state (defaults to "email" for Iteration 1)
  const [verificationType, setVerificationType] = useState("email");

  // Wrapper-only gate; never shown on the admin sign-in form.
  const [isPasskeySubmitting, setIsPasskeySubmitting] = useState(false);
  const [passkeyNotice, setPasskeyNotice] = useState(null);
  const [passkeyError, setPasskeyError] = useState(null);
  const showPasskeyLogin = !isAdmin && isPasskeyUiAvailable();

  const handleLoginAttempt = async (code = null) => {
    setCurrentErrors([]);
    setIsSubmitting(true);
    setButtonState("loading");

    try {
      const referralCode = getReferralCookie();
      const apiBaseUrl = process.env.REACT_APP_API_DOMAIN;

      const requestBody = { email, password };
      if (code) requestBody.verification_code = code;
      if (referralCode) requestBody.referral_code = referralCode;

      const headers = { "Content-Type": "application/json" };
      if (isAdmin) headers["X-Custom-Admin"] = "True";

      const response = await fetch(`${apiBaseUrl}/auth/login`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify(requestBody),
      });

      const resData = await response.json();

      // 1. SUCCESS: Knox session issued
      if (response.status === 200) {
        trackSignIn();

        const userData = {
          user_name: resData.user_name,
          email: resData.email,
          user_role: resData.user_role,
          has_usable_password: resData.has_usable_password,
          preferences: resData.preferences,
          account_status: resData.account_status,
          stripe_connected_account: resData.stripe_connected_account,
          affiliate_status: resData.affiliate_status || null,
          affiliates_enabled: resData.affiliates_enabled || false,
          financial_insights: resData.financial_insights,
          ista: resData.ista || false,
        };

        // Reset stale CSRF token in memory
        ApiClient.initializeApiClient();

        // Update Redux state so user is marked authenticated
        store.dispatch(loginRdx(userData));

        void associateAuthenticatedInstallationIfNative();
        if (referralCode) deleteReferralCookie();

        setPopupStep("success");
        setIsSubmitting(false);

        const triggerRefetches = () => {
          store.dispatch(accountApi.util.invalidateTags(["UNAUTHORIZED"]));
          window.dispatchEvent(new Event("session-restored"));
        };

        if (isModalMode) {
          setTimeout(() => {
            handleLoginSuccess();
            triggerRefetches();
          }, 300);
        } else {
          handleLoginSuccess();
          triggerRefetches();
        }
        return;
      }

      // 2. MFA SETUP REQUIRED (Mandatory TOTP setup)
      if (response.status === 403 && resData.code === "MFA_SETUP_REQUIRED") {
        setMfaSetupToken(resData.mfa_setup_token);
        setPopupStep("totp-setup");
        setIsPopupOpen(true);
        setIsSubmitting(false);
        setButtonState("idle");
        return;
      }

      // 3. MFA CODE REQUIRED
      if (response.status === 401 && resData.code === "MFA_REQUIRED") {
        const mfaType =
          resData.mfa_type || resData.verification_type || "email";
        setVerificationType(mfaType);

        setPopupStep("verification");
        setIsPopupOpen(true);
        setIsSubmitting(false);
        setButtonState("idle");
        return;
      }

      // 4. INVALID CREDENTIALS OR CODE
      const errorMessage =
        resData.message ||
        resData.detail ||
        (resData.non_field_errors && resData.non_field_errors[0]) ||
        "An error occurred. Please try again.";

      setCurrentErrors([errorMessage]);
      setIsSubmitting(false);
      setButtonState("idle");
    } catch (err) {
      setCurrentErrors(["Network error. Please check your connection."]);
      setIsSubmitting(false);
      setButtonState("idle");
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleLoginAttempt();
  };

  const handleVerifyCode = (code) => {
    handleLoginAttempt(code);
  };

  const handleLoginSuccess = () => {
    setIsPopupOpen(false);
    if (!isModalMode) {
      navigate(redirectPath || "/");
    }
  };

  const handlePasskeyLogin = async () => {
    if (isPasskeySubmitting) return;
    setPasskeyNotice(null);
    setPasskeyError(null);
    setIsPasskeySubmitting(true);

    try {
      const result = await loginWithPasskey();
      if (result.ok) {
        handleLoginSuccess();
        return;
      }
      if (result.notice) {
        setPasskeyNotice(result.notice);
      } else if (result.error) {
        setPasskeyError(result.error);
      }
    } catch (error) {
      setPasskeyError("Network error. Please check your connection.");
    } finally {
      setIsPasskeySubmitting(false);
    }
  };

  const handleDismissError = () => setCurrentErrors([]);

  const handlePopupClose = () => {
    setIsPopupOpen(false);
    setPopupStep("idle");
    setMfaSetupToken(null);
  };

  return (
    <>
      <div className="auth-form">
        <form onSubmit={handleFormSubmit}>
          <div className="auth-form-content">
            {currentErrors.length > 0 && !isPopupOpen && (
              <>
                {currentErrors.map((err, index) => (
                  <SmallAlert
                    key={index}
                    alert="True"
                    title="Error"
                    type="error"
                    message={err}
                    dynamicSize
                    dismissOnClickOutside={true}
                    dismissOnFocusOutside={false}
                  />
                ))}
              </>
            )}

            <p className="auth-form-title">Sign in to your Plaintr account</p>

            <div className="auth-form-fields">
              <div className="form-field">
                <div className="email-input-container">
                  <input
                    type="email"
                    name="email"
                    className="form-input"
                    placeholder="Email"
                    autoComplete="off"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <img
                    src="/images/icons/email_blue_light.svg"
                    alt="Email"
                    className="email-icon"
                  />
                </div>
              </div>

              <div className="form-field">
                <div className="password-input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    className="form-input"
                    placeholder="Password"
                    autoComplete="off"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <img
                    src={
                      showPassword
                        ? "/images/icons/eye_open_blue.svg"
                        : "/images/icons/eye_closed_blue.svg"
                    }
                    alt="Toggle password"
                    className="password-toggle-icon"
                    onClick={() => setShowPassword(!showPassword)}
                  />
                </div>
              </div>

              <SmartButton
                text="Sign In"
                loadingText="Signing In..."
                successText="Welcome!"
                state={buttonState}
                isSubmitting={isSubmitting}
                type="submit"
              />

              {showPasskeyLogin && (
                <>
                  {passkeyNotice && (
                    <SmallAlert
                      alert="True"
                      title="Heads up"
                      type="info"
                      message={passkeyNotice}
                      dynamicSize
                      dismissOnClickOutside={true}
                    />
                  )}
                  {passkeyError && (
                    <SmallAlert
                      alert="True"
                      title="Error"
                      type="error"
                      message={passkeyError}
                      dynamicSize
                      dismissOnClickOutside={true}
                    />
                  )}
                  <button
                    type="button"
                    className="cta-button secondary"
                    onClick={handlePasskeyLogin}
                    disabled={isPasskeySubmitting}
                  >
                    {isPasskeySubmitting
                      ? "Waiting for passkey..."
                      : "Sign in with a passkey"}
                  </button>
                </>
              )}
            </div>
            <div className="auth-footer-container">
              <p className="auth-form-footer">
                Do not have an account?{" "}
                <strong className="strongLnk">
                  <Link to="/sign-up">Sign up</Link>
                </strong>
              </p>
              <Link
                to="/forgot-password"
                title="Forgot Password?"
                className="auth-forgot-password"
              >
                Forgot Password?
              </Link>
            </div>
          </div>
        </form>

        <VerificationModal
          isOpen={isPopupOpen}
          onClose={handlePopupClose}
          onConfirm={handleVerifyCode}
          onSuccess={handleLoginSuccess}
          email={email}
          step={popupStep}
          mfaSetupToken={mfaSetupToken}
          isLoading={isSubmitting}
          errors={currentErrors}
          onDismissError={handleDismissError}
          onSetupComplete={(completedCode) => handleLoginAttempt(completedCode)}
          verificationType={verificationType}
        />
      </div>
    </>
  );
}

export default AuthForm;
